import {groupBy, keys, mapToObj, pipe, sortBy} from 'remeda'
import {QuestionCfgCommon, SubqCfg, subqTypes} from './subq'
import {match} from 'ts-pattern'
import {doSafe} from '../promise'
import {keyBy, mapEntries} from '../collection'
import {notNull} from '../type'
import {QuestionResultsQuestion} from './question'

type SortableSubqCfg = Partial<Pick<SubqCfg, 'status' | 'subquestionType' | 'childrenEnabled'>> &
  Pick<SubqCfg, 'subquestionOrder' >

interface SortableSubq<TCfg extends SortableSubqCfg = SortableSubqCfg> {
  id: string
  cfg: TCfg
  created_at?: string
  done?: boolean
  order?: number
}

interface GroupableSubq<TCfg extends SortableSubqCfg = SortableSubqCfg> extends SortableSubq<TCfg> {
  parent_id?: string
}

export const showForecastsStates = ['all', 'incomplete', 'none'] as const
export type ShowForecastState = (typeof showForecastsStates)[number]

const subqStatusOrder = ({cfg}: {cfg: Pick<SortableSubqCfg, 'status'>}) =>
  match(cfg.status)
    .with('approved', 'resolved', 'paused', () => 0)
    .otherwise(() => Number.MAX_SAFE_INTEGER)

export namespace Subqs {
  /** sort either a group or a subq */
  export const sort = <T extends SortableSubq>(
    subqs: T[],
    {order, showForecasts}: {order?: string[]; showForecasts?: ShowForecastState}
  ): T[] =>
    sortBy(
      subqs,
      // r => (showForecastsState == 'incomplete' ? (r.done ? 1 : 0) : 0),
      subqStatusOrder,
      r => subqTypes.findIndex(t => t == r.cfg.subquestionType),
      r => doSafe((order ?? []).indexOf(r.id), i => (i >= 0 ? i : undefined)) ?? Number.MAX_SAFE_INTEGER,
      r => r.created_at ?? 'z'
    )

  export const sortGroups = <T extends SubqGroup>(groups: T[], {order}: {order?: string[]}): T[] =>
    sortBy(
      groups,
      r => (r.isRootQuestion ? 0 : 1),
      r => (r.isRootQuestion ? 0 : subqStatusOrder(r)),
      r => (r.isRootQuestion ? 0 : subqTypes.findIndex(t => t == r.cfg.subquestionType)),
      r =>
        r.isRootQuestion ? 0 : (
          (doSafe((order ?? []).indexOf(r.id), i => (i >= 0 ? i : undefined)) ?? Number.MAX_SAFE_INTEGER)
        ),
      r => (r.isRootQuestion ? 0 : (r.cfg.created_at ?? 'z'))
    )

  export type GroupParentQuestionCfg = Pick<QuestionCfgCommon, 'subquestionOrder' | 'title'>
  export type GroupQuestionCfg = GroupParentQuestionCfg & {id: string}

  export const group = <T extends GroupableSubq, TQ extends GroupQuestionCfg>(
    subqsRaw: T[],
    {
      question,
      showForecasts: showForecasts,
      debugName
    }: {question: TQ; showForecasts?: ShowForecastState; debugName?: string}
  ) => {
    const subqById = keyBy(subqsRaw, s => s.id)
    const byParent = {
      [question.id]: new Array<T>(),
      ...mapToObj(
        subqsRaw.filter(s => s.cfg.childrenEnabled),
        s => [s.id, new Array<T>()]
      ),
      ...groupBy(
        subqsRaw.filter(s => !s.parent_id || subqById[s.parent_id]?.cfg.childrenEnabled),
        s => s.parent_id ?? question.id
      )
    }

    const flattenSq = ({cfg, parent_id, id, created_at}: T): SubqCfgWithParent<T> => ({
      id,
      parent_id,
      created_at,
      ...cfg
    }) // its more concise this way. But we still use separate cfg when it might be used for persistence

    const subqGroups: SubqGroup<T, TQ>[] = mapEntries(byParent, (idRaw, subqsRaw) => {
      const id = idRaw as string
      const isRootQuestion = id == question.id
      const groupSubq = isRootQuestion ? undefined : doSafe(subqById[id], flattenSq)
      const parentCfg = groupSubq ?? question
      const groupCommon: GroupCommon<T> = {
        id,
        subqs: Subqs.sort(subqsRaw, {order: parentCfg.subquestionOrder, showForecasts: showForecasts}) as T[]
      }

      if (isRootQuestion)
        return {isRootQuestion, ...groupCommon, cfg: question, groupParentQ: question} satisfies SubqGroupRoot<T, TQ>

      if (!groupSubq) {
        console.warn(`${debugName} no subq with id ${id} found`)
        return undefined
      }
      const groupParentQ = groupSubq.parent_id ? doSafe(subqById[groupSubq.parent_id], flattenSq) : question
      if (groupParentQ == undefined) {
        console.warn(
          `${debugName} no parent subq with id ${groupSubq.parent_id} found. keys ${keys(subqById).join('|')}`
        )
        return undefined
      }
      return {
        isRootQuestion,
        ...groupCommon,
        cfg: groupSubq,
        groupParentQ: groupParentQ
      } satisfies SubqGroupChild<T>
    }).filter(notNull)
    return Subqs.sortGroups(subqGroups, {order: question.subquestionOrder})
  }

  type SubqCfgWithParent<TCfg extends GroupableSubq> = TCfg['cfg'] & {
    id: string
    parent_id?: string
    created_at?: string
  }

  type GroupCommon<T extends GroupableSubq> = {
    id: string
    subqs: T[]
  }
  type SubqGroupRoot<T extends GroupableSubq, TQ extends GroupQuestionCfg> = GroupCommon<T> & {
    isRootQuestion: true
    cfg: TQ
    groupParentQ: TQ
  }
  type SubqGroupChild<T extends GroupableSubq> = GroupCommon<T> & {
    isRootQuestion: false
    cfg: SubqCfgWithParent<T>
    groupParentQ: SubqCfgWithParent<T>
  }
  export type SubqGroup<T extends GroupableSubq = GroupableSubq, TQ extends GroupQuestionCfg = GroupQuestionCfg> =
    | SubqGroupRoot<T, TQ>
    | SubqGroupChild<T>

  export const isRootGroup = <T extends GroupableSubq, TQ extends GroupQuestionCfg>(
    g: SubqGroup<T, TQ>
  ): g is SubqGroupRoot<T, TQ> => g.isRootQuestion
  export const isChildGroup = <T extends GroupableSubq, TQ extends GroupQuestionCfg>(
    g: SubqGroup<T, TQ>
  ): g is SubqGroupChild<T> => !g.isRootQuestion
}
